import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AgriLandApplication, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const PRODUCTION_TYPES = ['CROP_FARMING', 'LIVESTOCK', 'POULTRY', 'DAIRY', 'APICULTURE', 'HORTICULTURE', 'MIXED_FARMING'] as const;

/** M33 Agriculture — state agricultural land bank applications: GeoServe site check, committee scoring, M18 lease linkage. */
export function AgriLand() {
  const { persona } = useAuth();
  const isAgriOfficer = persona ? persona.roles.includes('AGRI_OFFICER') : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m33/agri-land-applications?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<AgriLandApplication>>(path);

  return (
    <div>
      <PageHeader
        title="Agri-Land Applications"
        subtitle="State agricultural land bank — GeoServe site eligibility check, scoring committee, M18 lease linkage (M33)."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New application
          </button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="App ref, parcel no…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['APPLIED', 'SITE_CHECKED', 'SCORED', 'APPROVED', 'REJECTED', 'LEASE_PENDING', 'ACTIVE', 'WITHDRAWN'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>
      {!isAgriOfficer ? <RoleNotice roles={['AGRI_OFFICER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No land bank applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">App ref</th>
                <th className="table-head">Parcel</th>
                <th className="table-head">Proposed use</th>
                <th className="table-head">Area (ha)</th>
                <th className="table-head">Score</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(a.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{a.appRef}</td>
                  <td className="table-cell font-mono text-xs">{a.parcelNo}</td>
                  <td className="table-cell text-xs">{a.proposedUse.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{a.requestedAreaHectares}</td>
                  <td className="table-cell text-xs">{a.scoreTotal ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <ApplicationDetailModal
          id={selectedId}
          isAgriOfficer={isAgriOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
      {showCreate ? (
        <NewApplicationModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ApplicationDetailModal({ id, isAgriOfficer, onClose, onChanged }: { id: string; isAgriOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const aq = useApiQuery<AgriLandApplication>(`/api/v1/m33/agri-land-applications/${id}`);
  const a = aq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAction, setShowAction] = useState<'score' | 'approve' | 'reject' | 'lease' | null>(null);
  const [scoreTotal, setScoreTotal] = useState('');
  const [note, setNote] = useState('');
  const [leaseRef, setLeaseRef] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      aq.reload();
      onChanged();
      setShowAction(null);
      setNote('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const withdraw = () => call(() => api.post(`/api/v1/m33/agri-land-applications/${id}/withdraw`));
  const confirm = () =>
    call(() => {
      if (showAction === 'score') return api.post(`/api/v1/m33/agri-land-applications/${id}/score`, { scoreTotal: Number.parseFloat(scoreTotal) });
      if (showAction === 'approve') return api.post(`/api/v1/m33/agri-land-applications/${id}/review`, { approve: true, note: note || undefined });
      if (showAction === 'reject') return api.post(`/api/v1/m33/agri-land-applications/${id}/review`, { approve: false, rejectionReason: note });
      return api.post(`/api/v1/m33/agri-land-applications/${id}/lease-clearance`, { leaseRef, note });
    });

  return (
    <Modal title={a ? a.appRef : 'Agri-land application'} onClose={onClose} wide>
      {aq.error ? <ErrorNote message={aq.error} /> : null}
      {aq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {a ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={a.status} />} />
            <Detail label="Applicant" value={`${a.applicantType}:${a.applicantId}`} mono />
            <Detail label="Parcel" value={a.parcelNo} mono />
            <Detail label="Tenure (GeoServe)" value={a.tenureType ?? '—'} />
            <Detail label="District" value={a.district ?? '—'} />
            <Detail label="Requested area" value={`${a.requestedAreaHectares} ha`} />
            <Detail label="Proposed use" value={a.proposedUse.replaceAll('_', ' ')} />
            <Detail label="Score" value={a.scoreTotal ?? '—'} />
            <Detail label="Site checked" value={formatDate(a.siteCheckedAt)} />
          </div>
          <Detail label="Business plan summary" value={a.businessPlanSummary} />
          {a.rejectionReason ? <ErrorNote message={`Rejected: ${a.rejectionReason}`} /> : null}
          {a.leaseLinkStatus ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700">
              M18 lease link: {a.leaseLinkStatus}
              {a.leaseLinkRef ? ` (${a.leaseLinkRef})` : ''} {a.leaseClearedAt ? `— cleared ${formatDate(a.leaseClearedAt)}` : ''}
            </div>
          ) : null}

          {isAgriOfficer ? (
            <div className="border-t border-slate-100 pt-4">
              {!showAction ? (
                <div className="flex flex-wrap gap-2">
                  {a.status === 'SITE_CHECKED' ? (
                    <button className="btn-primary" disabled={busy} onClick={() => setShowAction('score')}>
                      Record committee score
                    </button>
                  ) : null}
                  {a.status === 'SCORED' ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowAction('approve')}>
                        Approve
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowAction('reject')}>
                        Reject
                      </button>
                    </>
                  ) : null}
                  {a.status === 'LEASE_PENDING' ? (
                    <button className="btn-success" disabled={busy} onClick={() => setShowAction('lease')}>
                      Record M18 lease clearance
                    </button>
                  ) : null}
                  {a.status !== 'APPROVED' && a.status !== 'ACTIVE' && a.status !== 'WITHDRAWN' ? (
                    <button className="btn-secondary" disabled={busy} onClick={withdraw}>
                      Withdraw
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  {showAction === 'score' ? (
                    <Field label="Total score (0-100)">
                      <input className="field-input" type="number" min="0" max="100" value={scoreTotal} onChange={(e) => setScoreTotal(e.target.value)} />
                    </Field>
                  ) : null}
                  {showAction === 'lease' ? (
                    <Field label="M18 lease reference">
                      <input className="field-input" value={leaseRef} onChange={(e) => setLeaseRef(e.target.value)} />
                    </Field>
                  ) : null}
                  {showAction !== 'score' ? (
                    <Field label={showAction === 'reject' ? 'Rejection reason' : 'Note'}>
                      <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                    </Field>
                  ) : null}
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowAction(null)}>
                      Cancel
                    </button>
                    <button className={showAction === 'reject' ? 'btn-danger' : 'btn-success'} disabled={busy} onClick={confirm}>
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewApplicationModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [applicantType, setApplicantType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [applicantId, setApplicantId] = useState('');
  const [parcelNo, setParcelNo] = useState('');
  const [proposedUse, setProposedUse] = useState<(typeof PRODUCTION_TYPES)[number]>('CROP_FARMING');
  const [businessPlanSummary, setBusinessPlanSummary] = useState('');
  const [requestedAreaHectares, setRequestedAreaHectares] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/agri-land-applications', {
        applicantType,
        applicantId,
        parcelNo,
        proposedUse,
        businessPlanSummary,
        requestedAreaHectares: Number.parseFloat(requestedAreaHectares),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New agri-land application" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Applicant type">
            <select className="field-input" value={applicantType} onChange={(e) => setApplicantType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="PERSON">Person</option>
              <option value="ENTITY">Entity</option>
            </select>
          </Field>
          <Field label={applicantType === 'PERSON' ? 'NIN' : 'Business ID'}>
            <input className="field-input" value={applicantId} onChange={(e) => setApplicantId(e.target.value)} />
          </Field>
        </div>
        <Field label="Parcel no. (verified live against C9 GeoServe — must be STATE_LAND/CROWN_LAND)">
          <input className="field-input" value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Proposed use">
            <select className="field-input" value={proposedUse} onChange={(e) => setProposedUse(e.target.value as (typeof PRODUCTION_TYPES)[number])}>
              {PRODUCTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Requested area (hectares)">
            <input className="field-input" type="number" step="0.1" value={requestedAreaHectares} onChange={(e) => setRequestedAreaHectares(e.target.value)} />
          </Field>
        </div>
        <Field label="Business plan summary">
          <textarea className="field-input" rows={3} value={businessPlanSummary} onChange={(e) => setBusinessPlanSummary(e.target.value)} />
        </Field>
        <button
          className="btn-primary w-full"
          disabled={busy || !applicantId || !parcelNo || businessPlanSummary.length < 10 || !requestedAreaHectares}
          onClick={submit}
        >
          {busy ? 'Submitting…' : 'Submit application (runs GeoServe site check)'}
        </button>
      </div>
    </Modal>
  );
}
